import { Link } from "react-router-dom";
import { Heart, ShoppingCart, Star } from "lucide-react";
import { Button } from "./ui/button";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import { cn } from "../lib/utils";

export default function ProductCard({ product }) {
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const { addToCart } = useCart();
  const inWishlist = isInWishlist(product.id);

  const handleWishlistClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (inWishlist) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product.id);
    }
  };

  const handleQuickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const defaultSize = product.sizes[Math.floor(product.sizes.length / 2)];
    const defaultColor = product.colors[0];
    addToCart(product.id, 1, defaultSize, defaultColor);
  };

  return (
    <Link
      to={`/product/${product.id}`}
      className="group block"
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative aspect-[3/4] overflow-hidden rounded-lg bg-cotton-100 mb-4">
        <img
          src={product.images[0]}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        
        {/* Brand Badge */}
        <div
          className={cn(
            "absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-medium text-white",
            product.category === "mens" ? "bg-brand-mens" : "bg-brand-boys"
          )}
        >
          {product.brand}
        </div>

        {/* Wishlist Button */}
        <button
          onClick={handleWishlistClick}
          className={cn(
            "absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all",
            inWishlist
              ? "bg-red-500 text-white"
              : "bg-white/90 text-foreground hover:bg-white"
          )}
          data-testid={`wishlist-btn-${product.id}`}
        >
          <Heart className={cn("w-4 h-4", inWishlist && "fill-current")} />
        </button>

        {/* Quick Add */}
        <div className="absolute bottom-3 left-3 right-3 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
          <Button
            onClick={handleQuickAdd}
            className="w-full bg-primary hover:bg-primary/90 rounded-full"
            data-testid={`quick-add-btn-${product.id}`}
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Quick Add
          </Button>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-1">
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 text-accent fill-accent" />
            <span className="text-sm font-medium">{product.avg_rating.toFixed(1)}</span>
          </div>
          <span className="text-muted-foreground text-sm">
            ({product.review_count} reviews)
          </span>
        </div>
        <h3 className="font-medium text-foreground group-hover:text-primary transition-colors line-clamp-1">
          {product.name}
        </h3>
        <p className="text-primary font-bold text-lg mt-1">
          ₹{product.price.toLocaleString("en-IN")}
        </p>
      </div>
    </Link>
  );
}
